import { useMemo } from 'react';
import { Button, Card } from 'react-bootstrap';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import { keywordResearchTooltips } from '@/app/KeywordResearch/tooltip/keywordResearchTooltips';
import Venn from '@/app/KeywordResearch/components/keyword-gap/Venn';

type HostItem = { host: string; i: number };
type VennSet = { sets: string[]; size: number };

type KeywordOverlapCardProps = {
  hosts: HostItem[];
  data: unknown;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
};

const numericMap = (value: unknown): Record<string, number> | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const entries = Object.entries(value as Record<string, unknown>).filter(
    (entry): entry is [string, number] => typeof entry[1] === 'number'
  );
  const keys = entries.map(([key]) => key);
  if (!entries.length || keys.every(key => key === 'code' || key === 'status')) {
    return null;
  }
  return Object.fromEntries(entries);
};

const toVennSets = (raw: unknown): VennSet[] => {
  if (!raw || typeof raw !== 'object') return [];
  const record = raw as Record<string, unknown>;
  const source = numericMap(record.data) ?? numericMap(record);
  if (!source) return [];
  return Object.entries(source)
    .map(([key, size]) => ({
      sets: key
        .split(',')
        .map(part => part.trim())
        .filter(Boolean),
      size
    }))
    .filter(entry => entry.sets.length > 0);
};

const OverlapPreload = ({ domains }: { domains: number }) => (
  <div className="placeholder-glow" aria-hidden>
    <div className="d-flex flex-wrap gap-2 mb-3">
      {Array.from({ length: Math.max(domains, 2) }, (_, index) => (
        <span
          key={index}
          className="placeholder rounded-pill"
          style={{ width: 132 + (index % 2) * 36, height: 32 }}
        />
      ))}
    </div>
    <div className="position-relative mx-auto" style={{ width: 280, height: 220 }}>
      <span
        className="placeholder rounded-circle position-absolute"
        style={{ width: 168, height: 168, top: 26, right: 18 }}
      />
      <span
        className="placeholder rounded-circle position-absolute"
        style={{ width: 168, height: 168, top: 26, left: 18, opacity: 0.55 }}
      />
    </div>
  </div>
);

const KeywordOverlapCard = ({
  hosts,
  data,
  isLoading,
  isError,
  onRetry
}: KeywordOverlapCardProps) => {
  const sets = useMemo(() => toVennSets(data), [data]);
  const domainCount = hosts.filter(item => item.host).length;

  return (
    <Card className="h-100 w-100">
      <FalconCardHeader
        title={
          <div>
            همپوشانی کلمات
            <HelpTooltip
              html={
                <div className="text-start">
                  {keywordResearchTooltips.gap.keywordOverlap}
                </div>
              }
            />
          </div>
        }
        titleTag="h6"
        className="py-2"
        light
      />
      <Card.Body style={{ minHeight: 360 }}>
        {isLoading ? (
          <OverlapPreload domains={domainCount} />
        ) : isError ? (
          <div className="d-flex flex-column align-items-center justify-content-center gap-2 h-100 text-center">
            <p className="text-700 fs--1 mb-0">همپوشانی کلمات بارگذاری نشد.</p>
            <Button size="sm" variant="falcon-default" onClick={onRetry}>
              تلاش دوباره
            </Button>
          </div>
        ) : sets.length === 0 ? (
          <p className="text-700 fs--1 mb-0 text-center py-5">
            همپوشانی برای این دامنه‌ها پیدا نشد.
          </p>
        ) : (
          <Venn submittedHosts={hosts} data={sets} />
        )}
      </Card.Body>
    </Card>
  );
};

export default KeywordOverlapCard;
